import React, { useEffect, useRef, useState } from 'react';
import { inboxApi, customerApi, vehicleApi, bookingApi, formatApiError } from '../services/api';
import {
  Envelope, EnvelopeOpen, MagnifyingGlass, Camera, UploadSimple, X,
  Trash, Sparkle, CheckCircle, Archive, ArrowClockwise, FileText,
  Calendar, User, Car, Tag, Plus, PaperPlaneTilt, Lightning, Clock
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const STATUS_LABEL = { open: 'Offen', done: 'Erledigt', archived: 'Archiviert' };
const CAT_COLORS = {
  red: 'bg-red-100 text-red-800', amber: 'bg-amber-100 text-amber-800',
  blue: 'bg-blue-100 text-blue-800', purple: 'bg-purple-100 text-purple-800',
  orange: 'bg-orange-100 text-orange-800', emerald: 'bg-emerald-100 text-emerald-800',
  gray: 'bg-gray-100 text-gray-800',
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE') : '—');
const fmtEUR = (v) => (v != null ? new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(v)) : null);

export default function Inbox() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: 'open', category: '', search: '' });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.category) params.category = filter.category;
      if (filter.search) params.search = filter.search;
      const [l, s, c] = await Promise.all([
        inboxApi.list(params),
        inboxApi.stats(),
        inboxApi.categories(),
      ]);
      setItems(l.data);
      setStats(s.data);
      setCategories(c.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.status, filter.category]);

  const submitSearch = (e) => { e.preventDefault(); load(); };

  const catByKey = (k) => categories.find(c => c.key === k);

  return (
    <div className="p-6 space-y-6" data-testid="inbox-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#111418]">Posteingang</h1>
          <p className="text-[#525866] mt-1">
            Papier-Post scannen, digitalisieren und mit KI automatisch auslesen lassen.
          </p>
        </div>
        <Button className="btn-primary" onClick={() => setUploadOpen(true)} data-testid="inbox-new-btn">
          <Plus weight="bold" size={16} className="mr-1" /> Post scannen / hochladen
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="Gesamt" value={stats.total} icon={Envelope} tone="gray" />
          <StatCard label="Offen" value={stats.open} icon={EnvelopeOpen} tone="red" />
          <StatCard label="Wiedervorlage fällig" value={stats.reminders_due} icon={Clock} tone="amber" />
          <StatCard label="Erledigt" value={stats.done} icon={CheckCircle} tone="emerald" />
          <StatCard label="Archiviert" value={stats.archived} icon={Archive} tone="gray" />
        </div>
      )}

      {/* Filter */}
      <div className="card-swiss p-3 flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="flex gap-1">
          {['open', 'done', 'archived', ''].map(s => (
            <button
              key={s || 'all'}
              onClick={() => setFilter(f => ({ ...f, status: s }))}
              className={`px-3 py-1.5 text-sm rounded transition ${filter.status === s ? 'bg-[#111418] text-white' : 'bg-gray-100 text-[#525866] hover:bg-gray-200'}`}
              data-testid={`filter-status-${s || 'all'}`}
            >
              {s ? STATUS_LABEL[s] : 'Alle'}
            </button>
          ))}
        </div>
        <select
          value={filter.category}
          onChange={e => setFilter(f => ({ ...f, category: e.target.value }))}
          className="px-3 py-1.5 border border-[#E2E4E9] rounded text-sm"
          data-testid="filter-category"
        >
          <option value="">Alle Kategorien</option>
          {categories.map(c => (
            <option key={c.key} value={c.key}>{c.label}</option>
          ))}
        </select>
        <form onSubmit={submitSearch} className="flex-1 flex gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A94A6]" />
            <Input
              type="text"
              placeholder="Volltextsuche über Absender, Betreff, OCR-Text, Aktenzeichen…"
              value={filter.search}
              onChange={e => setFilter(f => ({ ...f, search: e.target.value }))}
              className="pl-9"
              data-testid="inbox-search"
            />
          </div>
          <Button type="submit" variant="outline">Suchen</Button>
        </form>
      </div>

      {/* List */}
      {loading ? (
        <div className="card-swiss h-40 skeleton" />
      ) : items.length === 0 ? (
        <div className="card-swiss p-12 text-center" data-testid="inbox-empty">
          <Envelope weight="thin" size={64} className="mx-auto text-[#8A94A6] mb-4" />
          <p className="text-[#525866]">Kein Posteingang – legen Sie das erste Dokument an.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map(i => {
            const cat = catByKey(i.category);
            return (
              <div
                key={i.id}
                onClick={() => setDetailId(i.id)}
                className="card-swiss p-4 cursor-pointer hover:shadow-md transition"
                data-testid={`inbox-card-${i.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <p className="text-xs text-[#8A94A6]">{i.post_number} · {fmtDate(i.received_date || i.received_at)}</p>
                    <p className="font-semibold text-[#111418] truncate">{i.sender || 'Unbekannter Absender'}</p>
                  </div>
                  {cat && (
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold whitespace-nowrap ${CAT_COLORS[cat.color] || CAT_COLORS.gray}`}>
                      {cat.label.split(' ')[0]}
                    </span>
                  )}
                </div>
                <p className="text-sm text-[#525866] line-clamp-2 min-h-[2.5rem]">
                  {i.subject || i.summary || '—'}
                </p>
                <div className="flex items-center justify-between mt-3 text-xs text-[#8A94A6]">
                  <div className="flex items-center gap-2">
                    {i.amount != null && <span className="font-semibold text-[#111418]">{fmtEUR(i.amount)}</span>}
                    {i.reminder_at && <span className="inline-flex items-center gap-1"><Clock size={12}/>{fmtDate(i.reminder_at)}</span>}
                    {i.files?.length > 0 && <span className="inline-flex items-center gap-1"><FileText size={12}/>{i.files.length}</span>}
                  </div>
                  <span className={`px-2 py-0.5 rounded ${i.status === 'open' ? 'bg-red-50 text-red-700' : i.status === 'done' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                    {STATUS_LABEL[i.status] || i.status}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Dialogs */}
      {uploadOpen && (
        <InboxUploadDialog
          categories={categories}
          onClose={() => setUploadOpen(false)}
          onSaved={() => { setUploadOpen(false); load(); }}
        />
      )}
      {detailId && (
        <InboxDetailDialog
          id={detailId}
          categories={categories}
          onClose={() => setDetailId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone }) {
  const toneCls = {
    red: 'bg-red-50 text-red-700', amber: 'bg-amber-50 text-amber-700',
    emerald: 'bg-emerald-50 text-emerald-700', gray: 'bg-gray-100 text-gray-700',
  }[tone] || 'bg-gray-100 text-gray-700';
  return (
    <div className="card-swiss p-3 flex items-center gap-3">
      <div className={`p-2 rounded ${toneCls}`}><Icon size={20} weight="bold" /></div>
      <div>
        <p className="text-xs text-[#8A94A6]">{label}</p>
        <p className="text-xl font-bold text-[#111418]">{value}</p>
      </div>
    </div>
  );
}

/* ---------------------- Upload Dialog ---------------------- */
function InboxUploadDialog({ categories, onClose, onSaved }) {
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [files, setFiles] = useState([]); // File[]
  const [meta, setMeta] = useState({ sender: '', subject: '', category: '', received_date: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);

  useEffect(() => {
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setCameraOn(true);
    } catch (e) {
      toast.error('Kamera konnte nicht gestartet werden: ' + (e.message || e));
    }
  };
  const capture = () => {
    if (!videoRef.current) return;
    const v = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = v.videoWidth || 1280;
    canvas.height = v.videoHeight || 960;
    canvas.getContext('2d').drawImage(v, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(blob => {
      if (!blob) return;
      const f = new File([blob], `scan-${Date.now()}.jpg`, { type: 'image/jpeg' });
      setFiles(list => [...list, f]);
    }, 'image/jpeg', 0.9);
  };
  const closeCamera = () => {
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setCameraOn(false);
  };

  const handleFilesInput = (e) => {
    const list = Array.from(e.target.files || []);
    if (list.length) setFiles(prev => [...prev, ...list]);
    e.target.value = null;
  };

  const removeFile = (idx) => setFiles(list => list.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (files.length === 0 && !meta.sender && !meta.subject) {
      toast.error('Bitte mindestens einen Scan oder Absender/Betreff angeben');
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      Object.entries(meta).forEach(([k, v]) => { if (v) fd.append(k, v); });
      await inboxApi.create(fd);
      toast.success('Post gespeichert');
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
      closeCamera();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="inbox-upload-dialog">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2E4E9]">
          <div>
            <h2 className="text-xl font-bold">Post scannen / hochladen</h2>
            <p className="text-sm text-[#525866]">Datei-Upload oder direkt mit der Kamera aufnehmen. KI-Analyse kann später ausgelöst werden.</p>
          </div>
          <button onClick={() => { closeCamera(); onClose(); }} className="p-2 hover:bg-gray-100 rounded" data-testid="inbox-upload-close"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="inbox-upload-file-btn">
              <UploadSimple weight="bold" size={16} className="mr-2" /> Datei auswählen (PDF/Bild)
            </Button>
            <input ref={fileInputRef} type="file" multiple accept="image/*,application/pdf" onChange={handleFilesInput} className="hidden" data-testid="inbox-upload-file-input" />
            {!cameraOn ? (
              <Button variant="outline" onClick={openCamera} data-testid="inbox-upload-camera-btn">
                <Camera weight="bold" size={16} className="mr-2" /> Mit Kamera aufnehmen
              </Button>
            ) : (
              <Button variant="outline" onClick={closeCamera}>
                <X weight="bold" size={16} className="mr-2" /> Kamera stoppen
              </Button>
            )}
          </div>

          {cameraOn && (
            <div className="border border-[#E2E4E9] rounded-lg overflow-hidden bg-black">
              <video ref={videoRef} autoPlay playsInline className="w-full max-h-[300px] object-contain bg-black" />
              <div className="p-3 bg-white flex justify-center">
                <Button className="btn-primary" onClick={capture} data-testid="inbox-camera-capture">
                  <Camera weight="bold" size={16} className="mr-2" /> Aufnehmen
                </Button>
              </div>
            </div>
          )}

          {files.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-[#525866] mb-2">Angehängte Scans ({files.length})</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {files.map((f, idx) => {
                  const isImg = f.type.startsWith('image/');
                  const url = isImg ? URL.createObjectURL(f) : null;
                  return (
                    <div key={idx} className="relative border border-[#E2E4E9] rounded overflow-hidden bg-[#FAFAFB]">
                      {isImg
                        ? <img src={url} alt="" className="w-full h-24 object-cover" />
                        : <div className="w-full h-24 flex items-center justify-center text-[#8A94A6]"><FileText size={32} /></div>}
                      <button onClick={() => removeFile(idx)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 hover:bg-black" data-testid={`inbox-file-remove-${idx}`}>
                        <X size={12} weight="bold" />
                      </button>
                      <div className="px-2 py-1 text-[10px] text-[#525866] truncate">{f.name}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-[#525866] mb-1">Absender (optional)</label>
              <Input value={meta.sender} onChange={e => setMeta(m => ({ ...m, sender: e.target.value }))} data-testid="inbox-meta-sender" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#525866] mb-1">Betreff (optional)</label>
              <Input value={meta.subject} onChange={e => setMeta(m => ({ ...m, subject: e.target.value }))} data-testid="inbox-meta-subject" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#525866] mb-1">Kategorie (optional)</label>
              <select value={meta.category} onChange={e => setMeta(m => ({ ...m, category: e.target.value }))} className="w-full px-3 py-2 border border-[#E2E4E9] rounded" data-testid="inbox-meta-category">
                <option value="">— keine —</option>
                {categories.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#525866] mb-1">Empfangsdatum</label>
              <Input type="date" value={meta.received_date} onChange={e => setMeta(m => ({ ...m, received_date: e.target.value }))} data-testid="inbox-meta-received" />
            </div>
          </div>

          <p className="text-xs text-[#8A94A6]">
            <Sparkle size={12} className="inline mr-1" /> Nach dem Speichern kannst du im Detail-Dialog die KI-Analyse anstoßen, um Absender, Betreff, Kategorie und Volltext automatisch zu extrahieren.
          </p>
        </div>

        <div className="flex justify-end gap-2 px-6 py-3 border-t border-[#E2E4E9]">
          <Button variant="outline" onClick={() => { closeCamera(); onClose(); }}>Abbrechen</Button>
          <Button className="btn-primary" onClick={handleSave} disabled={saving} data-testid="inbox-upload-save">
            {saving ? 'Speichere…' : 'Speichern'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------- Detail Dialog ---------------------- */
function InboxDetailDialog({ id, categories, onClose, onChanged }) {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [tab, setTab] = useState('preview');   // preview | edit | ocr
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const addFileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await inboxApi.get(id);
      setItem(r.data);
    } catch (err) { toast.error(formatApiError(err)); onClose(); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    (async () => {
      try {
        const [c, v, b] = await Promise.all([
          customerApi.getAll(),
          vehicleApi.getAll(),
          bookingApi.getAll(),
        ]);
        setCustomers(c.data || []);
        setVehicles(v.data || []);
        setBookings(b.data || []);
      } catch { /* ignore */ }
    })();
  }, []);

  if (loading || !item) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-white rounded-lg shadow-xl p-12 text-center">Lade…</div>
      </div>
    );
  }

  const patch = async (partial) => {
    setSaving(true);
    try {
      const r = await inboxApi.update(id, partial);
      setItem(r.data);
      onChanged && onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const r = await inboxApi.analyze(id);
      setItem(r.data.letter);
      toast.success('KI-Analyse abgeschlossen', {
        description: `Absender: ${r.data.suggested.sender || '—'}`,
        duration: 6000,
      });
      onChanged && onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAnalyzing(false); }
  };

  const doAction = async (fn, msg) => {
    try { await fn(); toast.success(msg); await load(); onChanged && onChanged(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const attachFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = null;
    if (!f) return;
    const fd = new FormData();
    fd.append('file', f);
    try {
      await inboxApi.attachFile(id, fd);
      toast.success('Datei angehängt');
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const cat = categories.find(c => c.key === item.category);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="inbox-detail-dialog">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2E4E9]">
          <div>
            <p className="text-xs text-[#8A94A6]">{item.post_number} · Eingegangen {fmtDate(item.received_date || item.received_at)}</p>
            <h2 className="text-xl font-bold text-[#111418]">{item.subject || item.sender || 'Ohne Betreff'}</h2>
            <div className="flex items-center gap-2 mt-1">
              {cat && <span className={`text-xs px-2 py-0.5 rounded font-semibold ${CAT_COLORS[cat.color]}`}>{cat.label}</span>}
              <span className={`text-xs px-2 py-0.5 rounded ${item.status === 'open' ? 'bg-red-50 text-red-700' : item.status === 'done' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>{STATUS_LABEL[item.status]}</span>
              {item.ai_analyzed_at && <span className="text-[10px] text-emerald-700 inline-flex items-center gap-1"><Sparkle size={10}/> KI analysiert</span>}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded" data-testid="inbox-detail-close"><X size={20} /></button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-6 pt-2 border-b border-[#E2E4E9]">
          {[['preview', 'Scan'], ['edit', 'Bearbeiten'], ['ocr', 'OCR-Text']].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-3 py-2 text-sm ${tab === k ? 'border-b-2 border-[#E60000] text-[#111418] font-semibold' : 'text-[#525866] hover:text-[#111418]'}`}
              data-testid={`inbox-tab-${k}`}
            >{l}</button>
          ))}
        </div>

        <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
          {tab === 'preview' && (
            <div>
              {item.files?.length === 0 ? (
                <p className="text-sm text-[#8A94A6]">Keine Datei angehängt.</p>
              ) : (
                <div className="space-y-3">
                  {item.files.map(f => (
                    <div key={f.file_id} className="border border-[#E2E4E9] rounded overflow-hidden">
                      <div className="flex items-center justify-between px-3 py-2 bg-[#FAFAFB]">
                        <span className="text-sm truncate">{f.filename}</span>
                        <div className="flex gap-2">
                          <a href={inboxApi.fileUrl(id, f.file_id)} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-600 hover:underline">Öffnen</a>
                          <button onClick={() => doAction(() => inboxApi.removeFile(id, f.file_id), 'Datei entfernt')} className="text-xs text-red-600 hover:underline" data-testid={`inbox-file-del-${f.file_id}`}>Löschen</button>
                        </div>
                      </div>
                      {f.content_type?.startsWith('image/') ? (
                        <img src={inboxApi.fileUrl(id, f.file_id)} alt="" className="w-full max-h-[500px] object-contain bg-black" />
                      ) : (
                        <iframe src={inboxApi.fileUrl(id, f.file_id)} title={f.filename} className="w-full h-[500px]" />
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-3">
                <Button variant="outline" onClick={() => addFileRef.current?.click()} data-testid="inbox-detail-add-file">
                  <UploadSimple weight="bold" size={14} className="mr-1" /> Weitere Seite anhängen
                </Button>
                <input ref={addFileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={attachFile} />
              </div>
            </div>
          )}

          {tab === 'edit' && (
            <EditForm item={item} categories={categories} customers={customers} vehicles={vehicles} bookings={bookings} saving={saving} onSave={patch} />
          )}

          {tab === 'ocr' && (
            <div>
              {item.summary && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-3 text-sm">
                  <b>KI-Zusammenfassung:</b> {item.summary}
                </div>
              )}
              {item.ocr_text ? (
                <pre className="whitespace-pre-wrap text-xs bg-[#FAFAFB] border border-[#E2E4E9] rounded p-3 font-mono">{item.ocr_text}</pre>
              ) : (
                <p className="text-sm text-[#8A94A6]">Kein OCR-Text vorhanden. Nutze „KI-Analyse starten" um den Text auszulesen.</p>
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex flex-wrap justify-between gap-2 px-6 py-3 border-t border-[#E2E4E9] bg-white">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={analyze} disabled={analyzing || (item.files?.length || 0) === 0} data-testid="inbox-analyze-btn">
              <Sparkle weight="bold" size={14} className="mr-1" /> {analyzing ? 'analysiere…' : (item.ai_analyzed_at ? 'Erneut mit KI analysieren' : 'KI-Analyse starten')}
            </Button>
            <Button variant="outline" onClick={() => doAction(() => inboxApi.createReply(id), 'Antwort-Brief-Entwurf erstellt')} data-testid="inbox-reply-btn">
              <PaperPlaneTilt weight="bold" size={14} className="mr-1" /> Antwort erstellen
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {item.status !== 'done' && (
              <Button variant="outline" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                onClick={() => doAction(() => inboxApi.markDone(id), 'Als erledigt markiert')} data-testid="inbox-done-btn">
                <CheckCircle weight="bold" size={14} className="mr-1" /> Erledigt
              </Button>
            )}
            {item.status !== 'archived' && (
              <Button variant="outline" onClick={() => doAction(() => inboxApi.archive(id), 'Archiviert')} data-testid="inbox-archive-btn">
                <Archive weight="bold" size={14} className="mr-1" /> Archivieren
              </Button>
            )}
            {item.status !== 'open' && (
              <Button variant="outline" onClick={() => doAction(() => inboxApi.reopen(id), 'Wiedereröffnet')}>
                <ArrowClockwise weight="bold" size={14} className="mr-1" /> Wiedereröffnen
              </Button>
            )}
            <Button variant="outline" className="text-red-700 border-red-300 hover:bg-red-50"
              onClick={async () => {
                if (!window.confirm('Diesen Brief wirklich löschen? Angehängte Scans werden mit gelöscht.')) return;
                await doAction(() => inboxApi.remove(id), 'Gelöscht');
                onClose();
              }}
              data-testid="inbox-delete-btn"
            >
              <Trash weight="bold" size={14} className="mr-1" /> Löschen
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EditForm({ item, categories, customers, vehicles, bookings, saving, onSave }) {
  const [form, setForm] = useState({
    sender: item.sender || '',
    sender_address: item.sender_address || '',
    subject: item.subject || '',
    category: item.category || '',
    reference_number: item.reference_number || '',
    received_date: item.received_date || '',
    amount: item.amount ?? '',
    due_date: item.due_date || '',
    reminder_at: item.reminder_at || '',
    notes: item.notes || '',
    linked_customer_id: item.linked_customer_id || '',
    linked_vehicle_id: item.linked_vehicle_id || '',
    linked_booking_id: item.linked_booking_id || '',
  });
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = () => {
    const payload = { ...form };
    payload.amount = payload.amount === '' ? null : Number(payload.amount);
    onSave(payload);
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Absender"><Input value={form.sender} onChange={e => upd('sender', e.target.value)} data-testid="edit-sender"/></Field>
        <Field label="Betreff"><Input value={form.subject} onChange={e => upd('subject', e.target.value)} data-testid="edit-subject"/></Field>
        <Field label="Absender-Adresse"><Input value={form.sender_address} onChange={e => upd('sender_address', e.target.value)}/></Field>
        <Field label="Aktenzeichen / Referenz"><Input value={form.reference_number} onChange={e => upd('reference_number', e.target.value)}/></Field>
        <Field label="Kategorie">
          <select value={form.category} onChange={e => upd('category', e.target.value)} className="w-full px-3 py-2 border border-[#E2E4E9] rounded" data-testid="edit-category">
            <option value="">— keine —</option>
            {categories.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </Field>
        <Field label="Empfangsdatum"><Input type="date" value={form.received_date} onChange={e => upd('received_date', e.target.value)}/></Field>
        <Field label="Betrag (€)"><Input type="number" step="0.01" value={form.amount} onChange={e => upd('amount', e.target.value)}/></Field>
        <Field label="Zahlungsziel / Frist"><Input type="date" value={form.due_date} onChange={e => upd('due_date', e.target.value)}/></Field>
        <Field label="Wiedervorlage am"><Input type="date" value={form.reminder_at} onChange={e => upd('reminder_at', e.target.value)} data-testid="edit-reminder"/></Field>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="Verknüpft: Kunde">
          <select value={form.linked_customer_id} onChange={e => upd('linked_customer_id', e.target.value)} className="w-full px-3 py-2 border border-[#E2E4E9] rounded" data-testid="edit-linked-customer">
            <option value="">— keine —</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.company_name || `${c.first_name || ''} ${c.last_name || ''}`.trim()}</option>)}
          </select>
        </Field>
        <Field label="Verknüpft: Fahrzeug">
          <select value={form.linked_vehicle_id} onChange={e => upd('linked_vehicle_id', e.target.value)} className="w-full px-3 py-2 border border-[#E2E4E9] rounded">
            <option value="">— keines —</option>
            {vehicles.map(v => <option key={v.id} value={v.id}>{v.brand} {v.model} · {v.license_plate}</option>)}
          </select>
        </Field>
        <Field label="Verknüpft: Buchung">
          <select value={form.linked_booking_id} onChange={e => upd('linked_booking_id', e.target.value)} className="w-full px-3 py-2 border border-[#E2E4E9] rounded">
            <option value="">— keine —</option>
            {bookings.map(b => <option key={b.id} value={b.id}>{b.booking_number}</option>)}
          </select>
        </Field>
      </div>

      <Field label="Notizen">
        <textarea rows={3} value={form.notes} onChange={e => upd('notes', e.target.value)} className="w-full px-3 py-2 border border-[#E2E4E9] rounded" data-testid="edit-notes"/>
      </Field>

      <div className="flex justify-end">
        <Button onClick={submit} disabled={saving} className="btn-primary" data-testid="inbox-edit-save">
          {saving ? 'Speichere…' : 'Änderungen speichern'}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-[#525866] mb-1">{label}</label>
      {children}
    </div>
  );
}
